import { api } from "../api";
// Mensagens são exibidas centralmente via interceptor em service/api
import {
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  AuthResponse,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateCurrentUserRequest,
  UsersResponse,
  UserRole,
  AccountsResponse,
} from "../../types/auth";
// HttpOnly cookies são gerenciados automaticamente pelo backend
import { refreshToken } from "../api";
import { invalidateCurrentSubscriptionCache } from "./subscriptions";
import { invalidatePaymentsCache } from "../payments";

let usersCache: UsersResponse | null = null;
let usersCacheAt = 0;
let pendingUsersRequest: Promise<UsersResponse> | null = null;
const USERS_CACHE_TTL_MS = 3000;
let currentUserCache: User | null = null;
let pendingCurrentUserRequest: Promise<User> | null = null;
let myAccountsCache: AccountsResponse | null = null;
let myAccountsCacheAt = 0;
let pendingMyAccountsRequest: Promise<AccountsResponse> | null = null;
const MY_ACCOUNTS_CACHE_TTL_MS = 30000;

export const invalidateUsersCache = (): void => {
  usersCache = null;
  usersCacheAt = 0;
};

export const invalidateCurrentUserCache = (): void => {
  currentUserCache = null;
  pendingCurrentUserRequest = null;
};

export const invalidateMyAccountsCache = (): void => {
  myAccountsCache = null;
  myAccountsCacheAt = 0;
  pendingMyAccountsRequest = null;
};

export const getCurrentUser = async (): Promise<User> => {
  if (currentUserCache) {
    return currentUserCache;
  }

  if (pendingCurrentUserRequest) {
    return pendingCurrentUserRequest;
  }

  pendingCurrentUserRequest = (async () => {
    try {
      const response = await api.get<User>("/users/me");
      currentUserCache = response.data;
      return response.data;
    } catch (error: any) {
      // Mensagem de erro será exibida pelo interceptor (se presente no body)
      throw error;
    } finally {
      pendingCurrentUserRequest = null;
    }
  })();

  return pendingCurrentUserRequest;
};

export const getMyAccounts = async (): Promise<AccountsResponse> => {
  if (
    myAccountsCache &&
    Date.now() - myAccountsCacheAt < MY_ACCOUNTS_CACHE_TTL_MS
  ) {
    return myAccountsCache;
  }

  if (pendingMyAccountsRequest) {
    return pendingMyAccountsRequest;
  }

  pendingMyAccountsRequest = (async () => {
    try {
      const response = await api.get<AccountsResponse>("/users/me/accounts");
      myAccountsCache = response.data;
      myAccountsCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      pendingMyAccountsRequest = null;
    }
  })();

  return pendingMyAccountsRequest;
};

export const updateCurrentUser = async (
  data: UpdateCurrentUserRequest,
): Promise<User> => {
  try {
    const response = await api.patch<User>("/users/me", data);
    currentUserCache = response.data;
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Função de login
export const getGoogleSocialLoginUrl = async (): Promise<{
  loginUrl: string;
}> => {
  try {
    const response = await api.get<{ loginUrl: string }>("/auth/google/login");
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error(
        "Endpoint de login social do Google não implementado no backend",
      );
    } else if (error.response?.status === 500) {
      throw new Error("Erro interno do servidor ao iniciar login com Google");
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(`Erro ao iniciar login com Google: ${error.message}`);
    } else {
      throw new Error("Erro desconhecido ao iniciar login com Google");
    }
  }
};

export const googleSocialCallback = async (
  authcode: string,
  state: string,
): Promise<{ userId?: string; email?: string; isNewUser?: boolean }> => {
  try {
    const response = await api.post("/auth/google/callback", {
      authcode,
      state,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Callback do Google não implementado no backend");
    } else if (error.response?.status === 500) {
      throw new Error("Erro interno do servidor ao finalizar login com Google");
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(`Erro ao finalizar login com Google: ${error.message}`);
    } else {
      throw new Error("Erro desconhecido ao finalizar login com Google");
    }
  }
};

export const loginUser = async (data: LoginRequest): Promise<AuthResponse> => {
  try {
    const response = await api.post("/auth/login", data);

    // Após login bem-sucedido, buscar dados do usuário atual
    try {
      const currentUser = await getCurrentUser();
      currentUserCache = currentUser;

      // Usar os dados do usuário atual (que inclui roles e outras informações)
      const user = {
        id: currentUser.id,
        accountId: currentUser.accountId,
        name: currentUser.name,
        email: currentUser.email,
        roles: currentUser.roles as UserRole[],
      };

      const account = response.data.account || {
        accountId: currentUser.accountId,
        name: data.email.split("@")[0],
        domain: "localhost:5000",
      };

      return {
        success: true,
        user: user,
        account: account,
      };
    } catch (userError: any) {
      // Se não conseguir buscar dados do usuário, verificar se é usuário órfão
      if (userError.response?.status === 403) {
        const code = userError.response?.data?.code as string | undefined;
        if (code === "NO_ACTIVE_ACCOUNT") {
          try {
            if (
              typeof window !== "undefined" &&
              !window.location.pathname.includes("/dash/contratar-conta/")
            ) {
              localStorage.setItem(
                "yuha_orphan",
                JSON.stringify(userError.response.data),
              );
            }
          } catch (e) {
            // ignore
          }

          const fallbackUser = response.data.user || {
            id: "user-id",
            name: data.email.split("@")[0],
            email: data.email,
          };

          const fallbackAccount = response.data.account || {
            name: data.email.split("@")[0],
            domain: "",
          };

          return {
            success: true,
            user: fallbackUser,
            account: fallbackAccount,
            isOrphan: true,
          };
        }
      }

      // Se não for órfão, usar dados básicos do response
      const user = response.data.user || {
        id: "user-id",
        name: data.email.split("@")[0],
        email: data.email,
      };

      const account = response.data.account || {
        name: data.email.split("@")[0],
        domain: "localhost:5000",
      };

      return {
        success: true,
        user: user,
        account: account,
      };
    }
  } catch (error: any) {
    throw error;
  }
};

// Função de registro
export const registerUser = async (
  data: RegisterRequest,
): Promise<AuthResponse> => {
  try {
    const response = await api.post("/auth/register", data);

    // Sucesso: mensagem será exibida se backend retornar `message`

    // Compatibilidade: permitir tanto o novo formato `{ user: {...}, account: {...} }`
    // quanto o formato antigo com campos no nível superior.
    const raw: any = data as any;
    const providedUser = raw.user || {
      name: raw.name,
      email: raw.email,
      password: raw.password,
    };

    // Se a API não retornar dados do usuário, usar o payload fornecido quando existir
    const user =
      response.data.user ||
      (providedUser
        ? {
            id: response.data?.user?.id || "user-id",
            name: providedUser.name,
            email: providedUser.email,
          }
        : undefined);

    // Normalizar/definir account para persistência local
    const account = response.data.account || {
      name: data.account?.name || (providedUser?.name ?? ""),
      domain: data.account?.domain || "",
    };

    // Armazenar email da primeira etapa para uso na segunda etapa (se disponível)
    try {
      if (providedUser?.email)
        localStorage.setItem("yuha_signup_email", providedUser.email);
    } catch (storageError) {
      // Erro ao armazenar email no localStorage
    }

    return {
      success: true,
      user: user,
      account: account,
    };
  } catch (error: any) {
    throw error;
  }
};

let pendingCheckAuth: Promise<boolean> | null = null;

// Verifica se existe uma sessão ativa (cookie HttpOnly válido). Deduplica
// chamadas concorrentes num só request (ex.: o StrictMode do React monta o
// efeito duas vezes em dev, ou duas partes da árvore checam ao mesmo tempo).
// `suppressErrorMessage`: usado nas telas públicas de entrada (login,
// cadastro, esqueci senha) — lá um 401 é esperado para o visitante anônimo e
// não deve virar um toast de erro.
export const checkAuth = (suppressErrorMessage = false): Promise<boolean> => {
  if (pendingCheckAuth) return pendingCheckAuth;

  pendingCheckAuth = (async () => {
    try {
      // Com HttpOnly cookies, fazer uma requisição simples para verificar autenticação
      const response = await api.get<User>("/users/me", {
        _suppressErrorMessage: suppressErrorMessage,
      });
      currentUserCache = response.data;
      return true;
    } catch (error: any) {
      // Se der erro 401, não está autenticado
      if (error.response?.status === 401) {
        invalidateCurrentSubscriptionCache();
        currentUserCache = null;
        return false;
      }

      // Se for 403 com código NO_ACTIVE_ACCOUNT, tratar como "órfão" autenticado
      if (error.response?.status === 403) {
        const code = error.response?.data?.code as string | undefined;
        if (code === "NO_ACTIVE_ACCOUNT") {
          try {
            if (
              typeof window !== "undefined" &&
              !window.location.pathname.includes("/dash/contratar-conta/")
            ) {
              localStorage.setItem(
                "yuha_orphan",
                JSON.stringify(error.response.data),
              );
            }
          } catch (e) {
            // ignore
          }
          invalidateCurrentSubscriptionCache();
          currentUserCache = null;
          return true;
        }
        return false;
      }

      // Para outros erros (rede, etc), assumir que está autenticado
      return true;
    } finally {
      pendingCheckAuth = null;
    }
  })();

  return pendingCheckAuth;
};

// Função de troca de senha
export const changePassword = async (
  data: ChangePasswordRequest,
): Promise<AuthResponse> => {
  try {
    const response = await api.post("/auth/change-password", data);

    // Sucesso: mensagem será exibida se backend retornar `message`

    return {
      success: true,
      message: "Senha alterada com sucesso!",
    };
  } catch (error: any) {
    let errorMessage = error.response?.data?.message || "Erro ao alterar senha";

    // Traduzir mensagens específicas da API para português
    if (errorMessage.toLowerCase().includes("invalid token")) {
      errorMessage =
        "Token inválido ou expirado. Solicite um novo link de redefinição.";
    } else if (errorMessage.toLowerCase().includes("password too weak")) {
      errorMessage = "Senha muito fraca. Use uma senha mais segura.";
    } else if (errorMessage.toLowerCase().includes("token expired")) {
      errorMessage = "O link de redefinição expirou. Solicite um novo link.";
    }
    return {
      success: false,
      message: errorMessage,
    };
  }
};

// Função de reset de senha
export const resetPassword = async (
  data: ResetPasswordRequest,
): Promise<AuthResponse> => {
  try {
    const response = await api.post("/auth/reset-password", data);

    // Sucesso: mensagem será exibida se backend retornar `message`

    return {
      success: true,
      message: "E-mail de redefinição enviado com sucesso!",
    };
  } catch (error: any) {
    let errorMessage =
      error.response?.data?.message || "Erro ao enviar e-mail de redefinição";

    // Traduzir mensagens específicas da API para português
    if (errorMessage.toLowerCase().includes("user not found")) {
      errorMessage = "E-mail não encontrado. Verifique se o e-mail está correto.";
    } else if (errorMessage.toLowerCase().includes("invalid email")) {
      errorMessage = "E-mail inválido. Digite um e-mail válido.";
    } else if (errorMessage.toLowerCase().includes("too many requests")) {
      errorMessage =
        "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
    }
    return {
      success: false,
      message: errorMessage,
    };
  }
};

// Funções da API para usuários
export const getUsers = async (): Promise<UsersResponse> => {
  if (usersCache && Date.now() - usersCacheAt < USERS_CACHE_TTL_MS) {
    return usersCache;
  }

  if (pendingUsersRequest) {
    return pendingUsersRequest;
  }

  pendingUsersRequest = (async () => {
    try {
      const response = await api.get<UsersResponse>("/users");
      usersCache = response.data;
      usersCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      // Erro: mensagem será exibida pelo interceptor (se presente no body)
      throw error;
    } finally {
      pendingUsersRequest = null;
    }
  })();

  return pendingUsersRequest;
};

export const createUser = async (data: CreateUserRequest): Promise<User> => {
  try {
    const response = await api.post<User>("/users", data);
    // Sucesso: mensagem será exibida se backend retornar `message`
    invalidateUsersCache();
    return response.data;
  } catch (error: any) {
    let errorMessage = error.response?.data?.message || "Erro ao criar usuário";

    // Traduzir mensagens específicas da API para português
    if (errorMessage.toLowerCase().includes("email already exists")) {
      errorMessage = "Este e-mail já está cadastrado. Use outro e-mail.";
    } else if (errorMessage.toLowerCase().includes("invalid email")) {
      errorMessage = "E-mail inválido. Digite um e-mail válido.";
    } else if (errorMessage.toLowerCase().includes("invalid roles")) {
      errorMessage = "Perfil inválido. Selecione um perfil válido.";
    }
    throw error;
  }
};

export const updateUser = async (
  id: string,
  data: UpdateUserRequest,
): Promise<User> => {
  try {
    const response = await api.put<User>(`/users/${id}`, data);
    // Sucesso: mensagem será exibida se backend retornar `message`
    invalidateUsersCache();
    return response.data;
  } catch (error: any) {
    let errorMessage =
      error.response?.data?.message || "Erro ao atualizar usuário";

    // Traduzir mensagens específicas da API para português
    if (errorMessage.toLowerCase().includes("user not found")) {
      errorMessage = "Usuário não encontrado.";
    } else if (errorMessage.toLowerCase().includes("invalid roles")) {
      errorMessage = "Perfil inválido. Selecione um perfil válido.";
    }
    throw error;
  }
};

// Trocar conta do usuário logado. O backend recria o JWT e salva no cookie.
// Após a troca, é recomendável recarregar a aplicação para evitar dados obsoletos.
export const switchAccount = async (accountId: string): Promise<boolean> => {
  try {
    await api.post("/users/me/switch-account", { accountId });
    invalidateCurrentUserCache();
    invalidateMyAccountsCache();
    invalidatePaymentsCache();

    // Forçar refresh de sessão (por segurança) - backend já atualizou cookie
    try {
      await refreshToken();
    } catch {
      // ignore
    }

    return true;
  } catch (error: any) {
    throw error;
  }
};

export const deactivateUser = async (id: string): Promise<void> => {
  try {
    await api.post(`/users/${id}/deactivate`, undefined, {
      _suppressErrorMessage: true,
    } as any);
    // Sucesso: mensagem será exibida se backend retornar `message`
    invalidateUsersCache();
  } catch (error: any) {
    let errorMessage =
      error.response?.data?.message || "Erro ao desativar usuário";

    if (errorMessage.toLowerCase().includes("user not found")) {
      errorMessage = "Usuário não encontrado.";
    }

    throw error;
  }
};

export const activateUser = async (id: string): Promise<void> => {
  try {
    await api.patch(`/users/${id}/activate`, undefined, {
      _suppressErrorMessage: true,
    } as any);
    // Sucesso: mensagem será exibida se backend retornar `message`
    invalidateUsersCache();
  } catch (error: any) {
    let errorMessage =
      error.response?.data?.message || "Erro ao ativar usuário";

    if (errorMessage.toLowerCase().includes("user not found")) {
      errorMessage = "Usuário não encontrado.";
    }

    throw error;
  }
};
